import {
  ActionSection,
  ExpandCloseSection,
  Icon,
  LogoIcon,
  MacHeader,
  NavigateArea,
  PlusIcon,
  TabCloseIcon,
  Tabs,
  TabsBarSection,
  TabsName,
  WindowsHeaderIcon
} from './styles';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import history from '../../history';
import { useLocation } from 'react-router-dom';
import { excludedTabBar, isElectronApp, isIpad } from '../../helpers/common';
import { rootStore } from '../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { MAX_TABS } from '../../mobx/stores/tabsStore';
import BackForwardIcon from './backForwardIcon';
import { ROUTES } from '../../routes/urls';
import { ICON_SIZE } from '../../global/constants';

// Type for the electron API methods we use in this component
type ElectronWindowApi = { windowAction?: (action: 'minimize' | 'fullscreen' | 'close') => void };

function TabsBar() {
  //Mobx store variables
  const { workspaceStore, appStore, chatMessageStore } = rootStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const { tabsStore } = workspace || {};
  const shouldUpdateTab = useRef(true);
  const { extractedDesktopView, isNavbarShow, isAppLoading } = appStore || {};
  const { chatGroups } = chatMessageStore || {};

  // Others
  const location = useLocation();
  const isReportPage = location.pathname.startsWith('/report');
  const isSettingsPage = location.pathname.startsWith('/settings');

  const onChangeMessageGroup = useCallback(
    async (groupId: string | null) => {
      const currentGroup = chatGroups?.find((x) => x?.id === groupId) || null;
      rootStore.chatMessageStore.setCurrentChatGroup(groupId ? currentGroup : chatGroups[0]);
    },
    [chatGroups]
  );

  const excludeTabs = useMemo(() => {
    return excludedTabBar(location.pathname);
  }, [location.pathname]);

  // Call function when tabs bar is shown in UI
  useEffect(() => {
    if (excludeTabs || !(isElectronApp() || isIpad())) return;

    let animationFrameId: number;
    let retryCount = 0;
    const maxRetries = 10; // 30 * 50ms = 1.5s max wait time
    const checkInterval = 50; // 50ms between checks

    const checkElementVisibility = () => {
      const tabsBarElement = document.querySelector('.app-header');

      if (tabsBarElement) {
        const rect = tabsBarElement.getBoundingClientRect();
        const isVisible =
          rect.height > 0 &&
          rect.width > 0 &&
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= window.innerHeight &&
          rect.right <= window.innerWidth;

        if (isVisible) {
          console.log('Tabs bar is now visible');
          rootStore.appStore.setIsTabBarLoad(true);
          return;
        }
      }

      retryCount = retryCount + 1;
      if (retryCount < maxRetries) {
        animationFrameId = window.setTimeout(() => {
          window.requestAnimationFrame(checkElementVisibility);
        }, checkInterval);
      } else {
        // Fallback: If we've tried enough times, just set it to true
        console.log('Fallback: Setting tab bar as loaded after max retries');
        rootStore.appStore.setIsTabBarLoad(true);
      }
    };

    // Start checking
    animationFrameId = window.requestAnimationFrame(checkElementVisibility);

    // Cleanup function
    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
        clearTimeout(animationFrameId);
      }
    };
  }, [excludeTabs]);

  useEffect(() => {
    if (excludeTabs || !isElectronApp()) return;
    if (!shouldUpdateTab.current) {
      shouldUpdateTab.current = true;
      return;
    }

    if (tabsStore) {
      tabsStore.onLocationChange();
    }
  }, [excludeTabs, location.pathname, location.search, tabsStore]);

  const onClickMinimize = useCallback(() => {
    const electronApi = (window as Window & { electronApi?: ElectronWindowApi }).electronApi;
    electronApi?.windowAction?.('minimize'); // minimize window
  }, []);

  const onClickFullScreen = useCallback(() => {
    const electronApi = (window as Window & { electronApi?: ElectronWindowApi }).electronApi;
    electronApi?.windowAction?.('fullscreen'); // toggle fullscreen
    // window.electronApi.windowAction(‘exit-fullscreen‘) // exit fullscreen
  }, []);

  const onClickClose = useCallback(() => {
    const electronApi = (window as Window & { electronApi?: ElectronWindowApi }).electronApi;
    electronApi?.windowAction?.('close'); // close window
  }, []);

  const onClickLogo = useCallback(() => {
    history.push(ROUTES.INBOX);
  }, []);

  const onClickSideBarToddle = useCallback((value: boolean) => {
    rootStore.appStore.setIsNavbarShow(value);
  }, []);

  if (!tabsStore) return <></>;

  const { tabs, selectedTabId } = tabsStore;

  const headerClass = [
    'app-header',
    tabs.length > 1 && isNavbarShow && !extractedDesktopView ? 'app-header-align-tabs-bar' : ''
  ].join(' ');

  return !excludeTabs && (isElectronApp() || isIpad()) ? (
    <MacHeader className={headerClass}>
      <ActionSection
        className={`back-forward-button ${
          isReportPage ? 'report-page' : isSettingsPage ? 'settings-page' : !isNavbarShow ? 'ipad-header' : ''
        }`}>
        {!isAppLoading && (
          <WindowsHeaderIcon
            role='button'
            onClick={() => onClickSideBarToddle(!isNavbarShow)}
            className='expand-sidebar-icon-mac'>
            <SVGIcon
              name='expand-sidebar-icon'
              width={ICON_SIZE.MD}
              height={ICON_SIZE.MD}
              viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
              fill='var(--text-secondary)'
            />
          </WindowsHeaderIcon>
        )}
        <NavigateArea className='navigate-area'>
          <LogoIcon role='button' onClick={onClickLogo} className='logo-icon'>
            <SVGIcon
              name='teamcamp-logo'
              width={ICON_SIZE.XXL}
              height={ICON_SIZE.XXL}
              viewBox={`0 0 ${ICON_SIZE.XXL} ${ICON_SIZE.XXL}`}
            />
          </LogoIcon>
          {!isAppLoading && (
            <WindowsHeaderIcon role='button' onClick={() => onClickSideBarToddle(!isNavbarShow)}>
              <SVGIcon
                name='expand-sidebar-icon'
                width={ICON_SIZE.MD}
                height={ICON_SIZE.MD}
                viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
                fill='var(--text-secondary)'
              />
            </WindowsHeaderIcon>
          )}
        </NavigateArea>
        {!isAppLoading && <BackForwardIcon />}
      </ActionSection>
      <TabsBarSection className={`tabs-bar-section`}>
        {!isAppLoading &&
          tabs.map((tab) => (
            <Tabs
              role='button'
              key={tab.id}
              isActive={selectedTabId === tab.id}
              onClick={() => tabsStore.handleTabClick(tab, onChangeMessageGroup)}
              isWithoutBg={tabs.length <= 1}>
              <TabsName>
                <Icon>
                  <SVGIcon
                    name={tab.icon}
                    width={ICON_SIZE.LG}
                    height={ICON_SIZE.LG}
                    viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                    fill='var(--text-primary)'
                    {...tab.iconProps}
                  />
                </Icon>
                <p>{tab.label}</p>
              </TabsName>
              {tabs.length > 1 && (
                <TabCloseIcon
                  role='button'
                  onClick={(event) => {
                    event.stopPropagation();
                    tabsStore.removeTab(tab.id);
                  }}
                  className='tab-close-icon'>
                  <SVGIcon
                    name='app-close-icon'
                    width={ICON_SIZE.SM}
                    height={ICON_SIZE.SM}
                    viewBox={`0 0 ${ICON_SIZE.SM} ${ICON_SIZE.SM}`}
                    fill='var(--text-secondary)'
                  />
                </TabCloseIcon>
              )}
            </Tabs>
          ))}
        {!isAppLoading && (
          <PlusIcon role='button' onClick={() => tabsStore.addTab()} isDisabled={tabs.length >= MAX_TABS}>
            <SVGIcon
              name='plus-icon'
              width={ICON_SIZE.LG}
              height={ICON_SIZE.LG}
              viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
              fill='var(--text-secondary)'
            />
          </PlusIcon>
        )}
        <ExpandCloseSection className='expand-close-section'>
          <WindowsHeaderIcon role='button' onClick={onClickMinimize}>
            <SVGIcon
              name='minimize-icon'
              width={ICON_SIZE.MD}
              height={ICON_SIZE.MD}
              viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
              fill='var(--text-secondary)'
            />
          </WindowsHeaderIcon>
          <WindowsHeaderIcon role='button' onClick={onClickFullScreen}>
            <SVGIcon
              name='expand-icon'
              width={ICON_SIZE.MD}
              height={ICON_SIZE.MD}
              viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
              fill='var(--text-secondary)'
            />
          </WindowsHeaderIcon>
          <WindowsHeaderIcon role='button' onClick={onClickClose}>
            <SVGIcon
              name='app-close-icon'
              width={ICON_SIZE.MD}
              height={ICON_SIZE.MD}
              viewBox={`0 0 ${ICON_SIZE.SM} ${ICON_SIZE.SM}`}
              fill='var(--text-secondary)'
            />
          </WindowsHeaderIcon>
        </ExpandCloseSection>
      </TabsBarSection>
    </MacHeader>
  ) : (
    <></>
  );
}

export default observer(TabsBar);
